<script setup>
import {ref, onMounted} from "vue";
import GlobusRadio from "@/components/UI/GlobusRadio.vue";
import ChevronDown from "@/components/icons/ChevronDown.vue";
import TagIcon from "@/components/icons/TagIcon.vue";
import TrashIcon from "@/components/icons/TrashIcon.vue";
import ChevronUp from "@/components/icons/ChevronUp.vue";

const props = defineProps({
  items: {
    type: Array,
    required: true,
    default: () => []
  },
  selected: {
    type: String,
    required: true,
    default: () => ""
  },
  selectedTag: {
    type: Object,
    required: true,
    default: () => ({})
  },
  tagList: {
    type: Array,
    required: true,
    default: () => []
  }
});

const isShowCategorySelector = ref(false);
const isShowTagSelector = ref(false);
const selectedTags = ref([]);

const emit = defineEmits(["select-category", "delete-category", "select-category-tag"]);

const cancelTag = (tag) => {
  const index = selectedTags.value.indexOf(tag);
  if (index !== -1) {
    selectedTags.value.splice(index, 1);
    emit("select-category-tag", selectedTags.value);
  }
};

onMounted(() => {
  if (props.selectedTag && Object.keys(props.selectedTag).length > 0) {
    selectedTags.value = [props.selectedTag];
  }
});
</script>

<template>
  <div class="wrapper">
    <div
      v-click-outside="() => isShowCategorySelector = false"
      :class="{'category-selector--active': isShowCategorySelector}"
      class="category-selector"
      @click.self="isShowCategorySelector = !isShowCategorySelector"
    >
      <div
        class="category-selector__title"
        @click.self="isShowCategorySelector = !isShowCategorySelector"
      >
        <TagIcon />
        {{ props.selected === "" ? "Без категории" : props.selected }}
        <ChevronDown @click.self="isShowCategorySelector = !isShowCategorySelector" />
        <TrashIcon
          class="category-selector__title-icon"
          @click="emit('delete-category')"
        />
      </div>
      <div
        v-if="isShowCategorySelector"
        class="category-selector__list"
      >
        <div
          v-for="item in props.items"
          :key="item"
          class="category-selector__item"
          :class="{'category-selector__item--active': item === props.selected}"
          @click="emit('select-category', item); isShowCategorySelector = false;"
        >
          <GlobusRadio :model-value="item === props.selected" />
          {{ item === "" ? "Без категории" : item }}
        </div>
      </div>
    </div>
    <div v-if="props.tagList.length > 0">
      <Dropdown
        v-model="selectedTags"
        :is-visible="isShowTagSelector"
        :items="props.tagList"
        label-key="name"
        width="100%"
        max-width="668px"
        class="rule-field--full"
        @update:model-value="args => emit('select-category-tag', args)"
        @close="isShowTagSelector = false"
      >
        <template #trigger>
          <div class="rule-row">
            <div 
              class="rule-field rule-field--full"
              :class="{ 'rule-field--active' : isShowTagSelector }"
            >
              <div
                class="rule-field__selector"
                @click="isShowTagSelector = !isShowTagSelector"
              >
                <span class="rule-field__value">
                  <template v-if="selectedTags.length > 0">
                    <Chip 
                      v-for="tag in selectedTags"
                      :key="tag.id"
                      :label="tag.name" 
                      color="gray"
                      @cancel="cancelTag(tag)"
                    />
                  </template>
                  <template v-else>Выберите тег</template>
                </span>
                <ChevronUp
                  v-if="isShowTagSelector"
                  class="rule-field__chevron rule-field__chevron--up"
                />
                <ChevronDown
                  v-else
                  class="rule-field__chevron rule-field__chevron--down"
                />
              </div>
            </div>
          </div>
        </template>
      </Dropdown>
    </div>
  </div>
</template>

<style scoped lang="scss">

.wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.category-selector {
  font-size: 14px;
  font-weight: 400;
  position: relative;
  display: flex;
  align-items: center;
  color: #7B87A5;
  gap: 2px;

  &__list {
    position: absolute;
    z-index: 2000;
    top: 110%;
    left: 0;
    display: flex;
    flex-direction: column;
    min-width: 254px;
    padding: 6px;
    border-radius: 8px;
    background: #FFFFFF;
    box-shadow: 1px 2px 10px 0 #131E2726;
    gap: 4px;
  }

  &__item {
    font-size: 14px;
    font-weight: 400;
    display: flex;
    align-items: center;
    height: 30px;
    padding: 0 14px;
    cursor: pointer;
    user-select: none;
    color: #373D3F;
    gap: 10px;
    border-radius: 8px;

    &:hover {
      background: #EEF1F4;
    }

    &--active {
      color: #1F9AFF;

      &:hover {
        background: #E0F0FF;
      }
    }
  }

  &__title {
    display: flex;
    align-items: center;
    width: 100%;
    cursor: pointer;
    user-select: none;
    gap: 5px;

    &-icon {
      margin-left: auto;
      color: #7B87A5;
    }
  }

  &--active {
    font-size: 14px;
    font-weight: 500;
    color: #1F9AFF;
  }
}

.rule-field {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FFF;
  border-radius: 8px;
  padding: 10px 12px;
  min-height: 40px;
  min-width: 0;
  overflow-x: hidden;
  border: 1px solid #fff;

  &--active {
    border-color: #1F9AFF;
  }
  
  &--error-status {
    border: 1px solid var(--system-red);
  }

  &__error-message {
    display: flex;
    margin-top: 10px;
    color: var(--system-red);
    font-size: 12px;
  }

  &--full {
    width: 100%;
  }

  &__label {
    font-size: 14px;
    white-space: nowrap;
    color: #373D3F;

    &--highlight {
      color: #1F9AFF;
    }
  }

  &__selector {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    flex: 1;
    justify-content: space-between;
    min-width: 0;

    &--right {
      margin-left: 0;
    }
  }

  &__value {
    font-size: 14px;
    color: var(--text-text-pur);
    display: flex;
    gap: 5px;
    flex-wrap: wrap;

    &--highlight {
      color: #1F9AFF;
    }
  }

  &__chevron {
    color: #CACACA;
    min-width: 14px;
    min-height: 14px;
    width: 14px;
    height: 14px;
    pointer-events: none;

    &--up {
      color: #1F9AFF;
    }
  }
}
</style>